import { Flex, Menu, Text } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import TreeNodeOptions from '@/components/shared/TreeNodeOptions';
import BaseBadge, { INSIGHT_BADGE_COLOR_MAP, UNKNOWN_INSIGHT_BADGE_COLOR } from '@/components/ui/BaseBadge';
import { ROUTES } from '@/configs/routes';
import { InsightEntity } from '@/entities/Insights';
import { isTopicNameKey } from '@/entities/Topics';
import { sessionStore } from '@/stores/global/RootStore';
import { useSidebarNoteStore } from '@/stores/local/sidebar/SidebarNoteStore';
import { useDevices } from '@/utils/devices';
import { getInsightStatus } from '@/utils/getInsightStatus';

import s from './Action.module.scss';

type Props = {
  model: InsightEntity;
};

const Actions: React.FC<Props> = ({ model }) => {
  const { isCompactViewport } = useDevices();

  const navigate = useNavigate();
  const { id } = useParams();
  const {
    insights: { removePopup, archivePopup, activatePopup },
  } = useSidebarNoteStore();
  const badgeColor = model.isArchived
    ? 'gray'
    : isTopicNameKey(model.topic.name)
      ? INSIGHT_BADGE_COLOR_MAP[model.topic.name]
      : UNKNOWN_INSIGHT_BADGE_COLOR;
  const status = getInsightStatus({
    isArchived: model.isArchived,
    isCreatedDate: model.createdAt === model.updatedAt,
    createdAt: model.createdAt,
    updatedAt: model.updatedAt,
  });
  const label = !model.isArchived ? 'Архивировать' : 'Сделать активным';
  const canEdit = sessionStore.userId != null && String(model.userId) === String(sessionStore.userId);

  const handleOpenRemovePopup = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
      event.stopPropagation();
      removePopup.open(model);
    },
    [model, removePopup],
  );

  const handleEdit = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
      event.stopPropagation();

      if (!id) {
        return;
      }

      navigate(ROUTES.organization.notebook.insights.insight.edit.create(id, String(model.id)), {
        state: {
          detailUpdatedAt: model.updatedAt,
          detailCreatedAt: model.createdAt,
          detailTitle: model.title,
        },
      });
    },
    [id, model.createdAt, model.id, model.title, model.updatedAt, navigate],
  );

  const handleOpenActionPopup = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
      event.stopPropagation();
      if (!model.isArchived) {
        archivePopup.open(model);
      } else {
        activatePopup.open(model);
      }
    },
    [model, archivePopup, activatePopup],
  );

  return (
    <>
      <BaseBadge color={badgeColor}>{model.topic.name}</BaseBadge>
      <Flex align="center" gap="sm">
        <Text size="xs" lh="xxs">
          {status}
        </Text>
        <span className={s['root__menu-slot']}>
          {canEdit && (
            <TreeNodeOptions
              dropdownClassName={s.root}
              items={
                <>
                  {!isCompactViewport && (
                    <Menu.Item className={s.root__item} onClick={handleEdit}>
                      <Text size="sm">Редактировать</Text>
                    </Menu.Item>
                  )}
                  <Menu.Item className={s.root__item} onClick={handleOpenActionPopup}>
                    <Text size="sm">{label}</Text>
                  </Menu.Item>
                  <Menu.Item className={s.root__item} onClick={handleOpenRemovePopup}>
                    <Text c="red" size="sm">
                      Удалить
                    </Text>
                  </Menu.Item>
                </>
              }
            />
          )}
        </span>
      </Flex>
    </>
  );
};

export default observer(Actions);
